"use client"

import { useState, useEffect, useMemo } from "react"
import {
  Modal,
  Box,
  TextField,
  Button,
  Typography,
  FormControlLabel,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  IconButton,
  InputAdornment,
  Grid,
  Chip,
  alpha,
  useTheme,
  Radio,
  RadioGroup,
  FormControl,
} from "@mui/material"
import SearchIcon from "@mui/icons-material/Search"
import ArrowForwardIcon from "@mui/icons-material/ArrowForward"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import PersonIcon from "@mui/icons-material/Person"
import CloseIcon from "@mui/icons-material/Close"
import PercentIcon from "@mui/icons-material/Percent"
import AttachMoneyIcon from "@mui/icons-material/AttachMoney"
import PriceChangeIcon from "@mui/icons-material/PriceChange"

const ModalActualizarPrecioEntidades = ({ open, handleClose, entidades, onUpdate }) => {
  const theme = useTheme()
  const [searchText, setSearchText] = useState("")
  const [selectedEntidades, setSelectedEntidades] = useState([])
  const [monto, setMonto] = useState("")
  const [tipoAjuste, setTipoAjuste] = useState("valor") // Por defecto, "valor"

  // Resetear estados cuando se cierra el modal
  useEffect(() => {
    if (!open) {
      setSelectedEntidades([])
      setMonto("")
      setTipoAjuste("valor")
      setSearchText("")
    }
  }, [open])

  // Filtrar entidades disponibles según el texto de búsqueda
  const filteredEntidades = useMemo(() => {
    // Asegurar que entidades es un array
    const entidadesArray = Array.isArray(entidades) ? entidades : []
    return entidadesArray.filter(
      (entidad) =>
        !selectedEntidades.includes(entidad.id) &&
        (entidad.descripcion?.toLowerCase().includes(searchText.toLowerCase()) ||
          (entidad.apellido && entidad.apellido.toLowerCase().includes(searchText.toLowerCase()))),
    )
  }, [entidades, selectedEntidades, searchText])

  // Entidades seleccionadas completas (con todos sus datos)
  const selectedEntidadesData = useMemo(() => {
    // Asegurar que entidades es un array
    const entidadesArray = Array.isArray(entidades) ? entidades : []
    return entidadesArray.filter((entidad) => selectedEntidades.includes(entidad.id))
  }, [entidades, selectedEntidades])

  // Manejar doble clic para agregar entidad
  const handleAddEntidad = (entidad) => {
    setSelectedEntidades((prev) => [...prev, entidad.id])
  }

  // Manejar doble clic para quitar entidad
  const handleRemoveEntidad = (entidad) => {
    setSelectedEntidades((prev) => prev.filter((id) => id !== entidad.id))
  }

  // Manejar actualización de precios
  const handleActualizarPrecios = () => {
    if (!monto || isNaN(monto) || selectedEntidades.length === 0) {
      alert("Debe seleccionar al menos una entidad y un monto válido.")
      return
    }

    // Llamamos a la función del padre pasando el array de entidades seleccionadas
    onUpdate(selectedEntidades, Number.parseFloat(monto), tipoAjuste)
    handleClose()
  }

  return (
    <Modal open={open} onClose={handleClose} aria-labelledby="modal-actualizar-precios">
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "90%", sm: 700, md: 900 },
          maxHeight: "90vh",
          bgcolor: "background.paper",
          borderRadius: 2,
          boxShadow: 24,
          p: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 2,
            bgcolor: alpha(theme.palette.primary.main, 0.05),
            borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h6" component="h2" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <PriceChangeIcon color="primary" />
            Actualizar Precios por Entidad
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Contenido */}
        <Box sx={{ p: 3, overflow: "auto", flex: 1 }}>
          {/* Sección de ajuste de precios */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Monto / Porcentaje"
                type="number"
                value={monto}
                onChange={(e) => setMonto(e.target.value)}
                fullWidth
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {tipoAjuste === "porcentaje" ? <PercentIcon /> : <AttachMoneyIcon />}
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl component="fieldset">
                <RadioGroup row value={tipoAjuste} onChange={(e) => setTipoAjuste(e.target.value)}>
                  <FormControlLabel value="valor" control={<Radio size="small" />} label="Valor fijo" />
                  <FormControlLabel value="porcentaje" control={<Radio size="small" />} label="Porcentaje" />
                </RadioGroup>
              </FormControl>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }}>
            <Chip label="Selección de Entidades" />
          </Divider>

          {/* Sistema de transferencia de entidades */}
          <Grid container spacing={2}>
            {/* Lista de entidades disponibles */}
            <Grid item xs={12} md={5}>
              <Typography variant="subtitle2" gutterBottom>
                Entidades Disponibles ({filteredEntidades.length})
              </Typography>

              <TextField
                fullWidth
                size="small"
                placeholder="Buscar entidades..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                sx={{ mb: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />

              <Paper
                variant="outlined"
                sx={{
                  height: 300,
                  overflow: "auto",
                  bgcolor: alpha(theme.palette.background.paper, 0.5),
                }}
              >
                <List dense component="div" role="list">
                  {filteredEntidades.length > 0 ? (
                    filteredEntidades.map((entidad) => (
                      <ListItem
                        key={entidad.id}
                        role="listitem"
                        button
                        onDoubleClick={() => handleAddEntidad(entidad)}
                        sx={{
                          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                          "&:hover": {
                            bgcolor: alpha(theme.palette.primary.main, 0.05),
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <PersonIcon fontSize="small" color="action" />
                        </ListItemIcon>
                        <ListItemText
                          primary={entidad.descripcion}
                          secondary={entidad.apellido}
                          primaryTypographyProps={{ variant: "body2" }}
                          secondaryTypographyProps={{ variant: "caption" }}
                        />
                      </ListItem>
                    ))
                  ) : (
                    <ListItem sx={{ justifyContent: "center", py: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        {searchText ? "No se encontraron resultados" : "No hay entidades disponibles"}
                      </Typography>
                    </ListItem>
                  )}
                </List>
              </Paper>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                Doble clic para agregar una entidad
              </Typography>
            </Grid>

            {/* Botones de transferencia */}
            <Grid
              item
              xs={12}
              md={2}
              sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}
            >
              <IconButton
                onClick={() => {
                  if (filteredEntidades.length > 0) {
                    handleAddEntidad(filteredEntidades[0])
                  }
                }}
                disabled={filteredEntidades.length === 0}
                sx={{
                  mb: 1,
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.main, 0.2),
                  },
                }}
              >
                <ArrowForwardIcon />
              </IconButton>

              <IconButton
                onClick={() => {
                  if (selectedEntidadesData.length > 0) {
                    handleRemoveEntidad(selectedEntidadesData[0])
                  }
                }}
                disabled={selectedEntidadesData.length === 0}
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.main, 0.2),
                  },
                }}
              >
                <ArrowBackIcon />
              </IconButton>
            </Grid>

            {/* Lista de entidades seleccionadas */}
            <Grid item xs={12} md={5}>
              <Typography variant="subtitle2" gutterBottom>
                Entidades Seleccionadas ({selectedEntidadesData.length})
              </Typography>

              <Paper
                variant="outlined"
                sx={{
                  height: 300,
                  overflow: "auto",
                  bgcolor: alpha(theme.palette.primary.main, 0.03),
                  borderColor: selectedEntidadesData.length > 0 ? alpha(theme.palette.primary.main, 0.3) : undefined,
                }}
              >
                <List dense component="div" role="list">
                  {selectedEntidadesData.length > 0 ? (
                    selectedEntidadesData.map((entidad) => (
                      <ListItem
                        key={entidad.id}
                        role="listitem"
                        button
                        onDoubleClick={() => handleRemoveEntidad(entidad)}
                        sx={{
                          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                          bgcolor: alpha(theme.palette.primary.main, 0.05),
                          "&:hover": {
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <PersonIcon fontSize="small" color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary={entidad.descripcion}
                          secondary={entidad.apellido}
                          primaryTypographyProps={{ variant: "body2" }}
                          secondaryTypographyProps={{ variant: "caption" }}
                        />
                      </ListItem>
                    ))
                  ) : (
                    <ListItem sx={{ justifyContent: "center", py: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        No hay entidades seleccionadas
                      </Typography>
                    </ListItem>
                  )}
                </List>
              </Paper>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                Doble clic para quitar una entidad
              </Typography>
            </Grid>
          </Grid>
        </Box>

        {/* Footer con botones de acción */}
        <Box
          sx={{
            p: 2,
            bgcolor: alpha(theme.palette.background.paper, 0.8),
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Button variant="outlined" onClick={handleClose}>
            Cancelar
          </Button>

          <Button
            variant="contained"
            color="primary"
            onClick={handleActualizarPrecios}
            disabled={selectedEntidades.length === 0 || !monto}
            startIcon={<PriceChangeIcon />}
          >
            Actualizar {selectedEntidades.length} {selectedEntidades.length === 1 ? "entidad" : "entidades"}
          </Button>
        </Box>
      </Box>
    </Modal>
  )
}

export default ModalActualizarPrecioEntidades

